import React, { useState, useEffect } from 'react';
import { Box, Toolbar, Typography, Button, Divider, IconButton, Chip, Tooltip } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import CaseDetail from '../../interfaces/caseDetail';
import DropDown from '../baseComponents/dropdown';
import AddCustomerDetailsModal from '../modals/addCustomerDetailModal';
import Case from '../../interfaces/case';
import CurrentCaseDetails from '../baseComponents/currentCaseDetails';
import Actions from '../actions/actionsApi';
import AdditionalCaseDetailsData from '../baseComponents/additionaCaseDetailsData';
import InteractionHistoryDetails from '../baseComponents/interactionHistoryDetails';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import DispositionSelector from '../baseComponents/dispositionSelector';
import { decodedJWT, fetchCaseDetailData, fetchCustomerDetailData, getOwnerType, getUserRole } from '../../utils/commonUtils';
import JWTTokenPayload from '../../interfaces/JWTTokenPayload';
import apiClient from '../../axiosClient';
import AudioProcessingRecord from '../../interfaces/audioProcessingRecord';
import { CustomerDetailResponse } from '../../interfaces/customerDetail';
import AdditionalContactReferences from '../baseComponents/additionalContactReferences';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import RoleManagement from '../../security/roleManagement';
import { setHeaderMenuDisabled, setNavMenuDisabled } from '../../store/slices/componentDisableSlice';
import DialerService from '../../services/dialerService';

const styles = {
	phoneContainer: {
		display: 'flex',
		gap: '16px',
		alignItems: 'center',
		marginBottom: '16px',
	},
	caseDetailBox: {
		paddingBottom: '4px'
	},
};

interface CaseDetailCardProps {
	selectedRow: Case;
	updateFocusMode: (agent_report: any) => void;
	updateCaseScore: (score: number) => void;
}

const CaseDetailCard: React.FC<CaseDetailCardProps> = ({ selectedRow, updateFocusMode, updateCaseScore }) => {
	const token: false | JWTTokenPayload = decodedJWT();
	const dispatch: AppDispatch = useDispatch();
	const [caseDetailData, setCaseDetailData] = useState<CaseDetail[]>([]);
	const [addCaseDetailModalOpen, setAddCaseDetailModalOpen] = useState<boolean>(false);
	const [contactList, setContactList] = useState<string[]>([]);
	const [refreshCaseDetailToken, setRefreshCaseDetailToken] = useState<boolean>(false);
	const [refresh, setRefresh] = useState<boolean>(false);
	const [customerDetail, setCustomerDetail] = useState<CustomerDetailResponse[]>([]);
	const [prefilledDisposition, setPrefilledDisposition] = useState<Record<string, any> | null>(null);
	const [audioProcessingRecord, setAudioProcessingRecord] = useState<AudioProcessingRecord | null>(null);
	const [selectedPhoneNumber, setSelectedPhoneNumber] = useState<string>('');
	const isDialerVisible = useSelector((state: RootState) => state.dialerState.isDialerVisible);
	

	useEffect(() => {
		if (audioProcessingRecord) {
			setPrefilledDisposition({
				"disposition_order": audioProcessingRecord.disposition_order,
				"disposition_inputs": audioProcessingRecord.disposition_inputs,
				"comment": audioProcessingRecord.summarized_comment,
				"auto_submit": false
			});
		}
	}, [audioProcessingRecord]);

	const onDispositionSuccess = async () => {
		// This is called to only update the interaction history
		setRefresh(!refresh);
		setPrefilledDisposition(null);
		try {
			const response = await apiClient.put(`/api/v1/agent_case_allocation/update_case_score/${selectedRow.case_id}`);
			if (response.status === 200) {
				updateCaseScore(response.data.score);
           		updateFocusMode(response.data.agent_report);
				alert('case score updated successfully');
			}
		} catch (error) {
			alert('Could not update case score: ' + error);
		}

		
		dispatch(setNavMenuDisabled(false));
		dispatch(setHeaderMenuDisabled(false));
	};

	useEffect(() => {
		fetchCaseDetailData(selectedRow, setCaseDetailData);
		fetchCustomerDetailData(selectedRow, setCustomerDetail, setContactList);
	}, [refreshCaseDetailToken, selectedRow.case_id]);

	const customerDetailModalClose = () => {
		setAddCaseDetailModalOpen(false);
		setRefreshCaseDetailToken(!refreshCaseDetailToken);
	};

	return (
		<>
			{addCaseDetailModalOpen && (
				<AddCustomerDetailsModal
					open={addCaseDetailModalOpen}
					onClose={customerDetailModalClose}
					selectedRow={selectedRow}
				/>
			)}
			<div >
				<Toolbar sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexDirection: 'row' }}>
					<Box display="flex" gap={3}>
						<Typography variant='h6'>{selectedRow.customer_name}</Typography>
						<Typography variant="body1" display="flex" alignItems="center" gap={1}>
							<span style={{ color: '#888' }}>Loan ID:</span>
							<span style={{ fontWeight: 500 }}>{selectedRow.client_reference_id}</span>
							<Tooltip title="Copy Loan ID">
								<IconButton
									size="small"
									onClick={() => navigator.clipboard.writeText(selectedRow.client_reference_id)}
								>
									<ContentCopyIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</Typography>
						{
							!RoleManagement.isAgent(getUserRole(token)) && RoleManagement.isAgency(getOwnerType(token)) &&

							<Typography variant="body1" display="flex" alignItems="center" >
								<span style={{ color: '#888' }}>Allocated Agents:</span>
								<Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
									{(selectedRow?.assigned_agents ?? []).map((agent, index) => (
										<Chip key={index} label={agent} />
									))}
								</Box>
							</Typography>
						}
					</Box>
				</Toolbar>
				<Divider />
				<div style={{ display: 'flex' }}>
					<div style={{ width: '30vw' }}>
						<Box sx={{ padding: 1, overflowY: 'auto', }}>
							<Box sx={styles.phoneContainer}>
								<IconButton disabled={!isDialerVisible || !selectedRow} onClick={() => DialerService.scheduleCallBack(selectedRow?.case_id || '', selectedPhoneNumber)}>
									<PhoneIcon />
								</IconButton>
								<Box sx={{ width: '13vw' }}>
									<DropDown
										label="Select Phone Number"
										selectedValue={selectedPhoneNumber}
										onSelection={val => setSelectedPhoneNumber(val)}
										items={contactList}
									/>
								</Box>
								<Button variant="outlined" onClick={() => setAddCaseDetailModalOpen(true)}>
									Add More Details
								</Button>
							</Box>
							<Box>
								<AdditionalContactReferences
									client_reference_id={selectedRow.client_reference_id}
									customerDetailData={customerDetail}
								/>
								<Divider />
								<div style={styles.caseDetailBox}>
									<CurrentCaseDetails refresh={refresh} case_id={selectedRow.case_id} caseDetailData={caseDetailData} />
								</div>
								<Divider sx={{ marginY: "8px" }} />
								<Actions
									setRefresh={setRefreshCaseDetailToken}
									case_id={selectedRow.case_id}
									client_name={selectedRow.client_name}
									refresh={refreshCaseDetailToken}
								/>
								<AdditionalCaseDetailsData
									caseDetailData={caseDetailData}
									customerDetailData={customerDetail}
								/>
								<Divider sx={{ marginY: "16px" }} />
							</Box>
						</Box>
					</div>
					<div style={{ width: '30vw', padding: "4px" }}>
						<DispositionSelector
							selectedRow={selectedRow}
							onDispositionSubmitSuccess={onDispositionSuccess}
							prefilledDisposition={prefilledDisposition}
							setAudioProcessingRecord={setAudioProcessingRecord}
							callLogId={null}
						/>
					</div >
					<div style={{ width: '30vw', padding: '4px', marginTop: 4 }} >
						<InteractionHistoryDetails
							refresh={refresh}
							client_name={selectedRow.client_name}
							client_reference_id={selectedRow.client_reference_id}
							case_id={selectedRow.case_id}
						/>
					</div>
				</div >
			</div>
		</>
	);
};


export default CaseDetailCard;
